import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isNative } from '../../platform/platform';
import { ScrollWrapper, ScrollContainer, Track, Thumb } from './scroll-area.styled';

type ScrollAreaProps = {
  maxHeight?: string | number;
  className?: string;
  children: React.ReactNode;
};

/**
 * Reusable scroll container.
 *
 * - Web/desktop: uses CSS-styled thin scrollbar.
 * - Native (Android/iOS): hides the WebView scrollbar and renders a
 *   lightweight JS-driven thumb that tracks scroll position.
 *   Thumb is always visible when content overflows.
 */
export const ScrollArea: React.FC<ScrollAreaProps> = ({ maxHeight, className, children }) => {
  const native = isNative();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [thumbHeight, setThumbHeight] = useState(0);
  const [thumbTop, setThumbTop] = useState(0);
  const [thumbVisible, setThumbVisible] = useState(false);
  const [needsScroll, setNeedsScroll] = useState(false);

  const mh = maxHeight != null
    ? typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight
    : undefined;

  // Recalculate whether content overflows + thumb dimensions
  const recalc = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollHeight, clientHeight, scrollTop } = el;
    const overflows = scrollHeight > clientHeight + 1; // +1 for rounding
    setNeedsScroll(overflows);
    setThumbVisible(overflows);

    if (!overflows) return;

    const ratio = clientHeight / scrollHeight;
    const hThumb = Math.max(20, ratio * clientHeight);
    const maxTop = clientHeight - hThumb;
    const scrollRatio = scrollTop / (scrollHeight - clientHeight);
    setThumbHeight(hThumb);
    setThumbTop(scrollRatio * maxTop);
  }, []);

  // Observe children size changes so thumb recalculates when items are added
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !native) return;

    recalc();

    const ro = new ResizeObserver(() => recalc());
    ro.observe(el);
    // Also observe first child if it exists (content changes)
    if (el.firstElementChild) ro.observe(el.firstElementChild);

    return () => ro.disconnect();
  }, [native, recalc, children]);

  const handleScroll = useCallback(() => {
    if (!native) return;
    recalc();
  }, [native, recalc]);

  return (
    <ScrollWrapper $maxHeight={mh} className={className}>
      <ScrollContainer ref={scrollRef} $native={native} onScroll={handleScroll}>
        {children}
      </ScrollContainer>

      {native && needsScroll && (
        <Track>
          <Thumb $height={thumbHeight} $top={thumbTop} $visible={thumbVisible} />
        </Track>
      )}
    </ScrollWrapper>
  );
};
